// Severity and alert-type presentation, lifted from the Andon app's
// `lib/severity.ts`. The one place a star tier becomes a colour, and the one
// place an alert type becomes an icon — so a new type is a single edit rather
// than a hunt through five screens.
//
// The hex values match the design and the server's own tiers. They are not in
// the palette because they are not theme colours: a 3★ alert is red in a light
// theme too, and "the line is stopped" is not something a preference should be
// able to soften.
//
// WHAT CHANGED IN THE LIFT. Every label here is a TR KEY rather than a string:
// the shop floor draws its chrome in three languages and the tile grid is
// chrome. The keys resolve through `useT()` at the point of drawing, so this
// file stays free of React and can be tested on its own. What is NOT
// translated is anything that travels to the server — the type key, the
// answer text "Not sure" — because the server resolves those by their text.

import type { AlertTypeKey, Stars } from '@/api/andon/types';
import type { IconName } from '@/components/ui/Icon';
import type { StringKey } from '@/i18n/strings';

export const STAR_COLOR: Record<Stars, string> = {
  3: '#ff5a5f',
  2: '#f0a13a',
  1: '#6e7367',
};

export const STAR_TINT: Record<Stars, string> = {
  3: 'rgba(255,90,95,.12)',
  2: 'rgba(240,161,58,.12)',
  1: 'rgba(144,146,137,.16)',
};

export const starLabel = (n: Stars): string => '★'.repeat(n);

/**
 * The three tiers, in the operator's words — as frame v2.12 fixes them
 * (script L930 `sev`): "Line is stopped ★★★", "Will stop soon ★★", "Needs
 * attention ★", three buttons in a row under "Is your line stopped right
 * now?". The stars are appended at the point of drawing.
 */
export const SEVERITY_COPY: Record<Stars, { label: StringKey }> = {
  3: { label: 'sevRowStoppedT' },
  2: { label: 'sevRowWillStopT' },
  1: { label: 'sevRowAttentionT' },
};

/** Worst first: the order the severity buttons are drawn in. */
export const SEVERITY_ORDER: Stars[] = [3, 2, 1];

export interface TypeTile {
  key: AlertTypeKey;
  label: StringKey;
  routes: StringKey;
  icon: IconName;
  color: string;
  tint: string;
  /** Folded away; drawn for old rows, never offered. */
  retired?: true;
}

/**
 * The tiles, in the order the design lays them out: the two that stop a line
 * first, then everything else. Which of them APPLY is the server's call (see
 * `raiseGrid`); this is how each one looks.
 *
 * The set is validated against 3,174 labelled historical andons; the evidence
 * for each is in the Andon repo's docs/andon-history-analysis.md. Three changes
 * worth knowing when reading this list:
 *
 *   upstream   456 records and 55% of the app's live usage, and the old tile
 *              set had no correct destination for any of them. The operator
 *              can always tell this from a missing part: nothing is missing
 *              from Stores, an internal station has not produced the thing.
 *   labour     106 records that previously landed in Need info or Need a call,
 *              both wrong.
 *   other      replaces Pod / access, which had 13 records and six hours of
 *              downtime over two years. It is a residual, and it routes to
 *              triage AND the WC manager — never to one person, which is what
 *              the old app's "Other" did with 282 records.
 *
 * Missing part is deliberately NOT split, though it is the worst-routed tile
 * at 42%. The operator does not know whether a part is off-site or merely
 * unreplenished, only that they do not have it; that split is retrieval's job
 * and belongs at root cause.
 */
export const TYPE_TILES: TypeTile[] = [
  { key: 'materials', label: 'tileMissingPartT', routes: 'routeMissingPartT', icon: 'box', color: '#f0a13a', tint: 'rgba(240,161,58,.14)' },
  { key: 'quality', label: 'tileQualityDefectT', routes: 'routeQualityDefectT', icon: 'shield', color: '#ff5a5f', tint: 'rgba(255,90,95,.13)' },
  { key: 'upstream', label: 'tileUpstreamT', routes: 'routeUpstreamT', icon: 'clock', color: '#f0a13a', tint: 'rgba(240,161,58,.14)' },
  { key: 'equipment', label: 'tileMachineDownT', routes: 'routeMachineDownT', icon: 'wrench', color: '#f0a13a', tint: 'rgba(240,161,58,.14)' },
  { key: 'other', label: 'tileSomethingElseT', routes: 'routeSomethingElseT', icon: 'more', color: '#6e7367', tint: 'rgba(144,146,137,.18)' },
  // RETIRED on 26 Sep 2026, kept only so an old row still draws with the label
  // the operator tapped. Never offered: the server's tile list decides what
  // the grid shows, and these are archived types in Odoo. Safety is still ON
  // the grid, as the link tile below rather than as an andon type.
  { key: 'safety', label: 'tileSafetyT', routes: 'routeRetiredT', icon: 'hazard', color: '#6e7367', tint: 'rgba(144,146,137,.18)', retired: true },
  { key: 'labour', label: 'tileRetiredLabourT', routes: 'routeRetiredT', icon: 'person', color: '#6e7367', tint: 'rgba(144,146,137,.18)', retired: true },
  { key: 'design', label: 'tileRetiredDesignT', routes: 'routeRetiredT', icon: 'drawing', color: '#6e7367', tint: 'rgba(144,146,137,.18)', retired: true },
  { key: 'info', label: 'tileRetiredInfoT', routes: 'routeRetiredT', icon: 'info', color: '#6e7367', tint: 'rgba(144,146,137,.18)', retired: true },
  { key: 'decision', label: 'tileRetiredDecisionT', routes: 'routeRetiredT', icon: 'list', color: '#6e7367', tint: 'rgba(144,146,137,.18)', retired: true },
  // TEMPORARY, and last on the grid by a wide margin. Grey rather than amber
  // or red: every colour on this grid means urgency, and this tile has none to
  // claim. Its `routes` line is the true one — nobody is coming — because that
  // string is a promise to whoever taps it, and a jokey one here would be the
  // single place in the app where the promise is a joke.
  //
  // Drawn only when the server lists it. See `tiles` on OperatorHomeVM.
  { key: 'test', label: 'tileTestT', routes: 'routeTestT', icon: 'gear', color: '#6e7367', tint: 'rgba(144,146,137,.18)' },
];

/**
 * The retired types' labels, for an old row that still names one.
 *
 * Derived from the tile list rather than written twice, so a retired tile and
 * the label drawn for its history can never disagree. The server's
 * `RETIRED_TYPES` carries one more key, `access`, which predates the client's
 * tile set and reaches this app only inside a server-built title.
 */
export const RETIRED_TYPES: Readonly<Partial<Record<AlertTypeKey, StringKey>>> = Object.fromEntries(
  TYPE_TILES.filter((t) => t.retired).map((t) => [t.key, t.label]),
);

/** The tile for a type, retired ones included. Undefined for a key this build has never heard of. */
export const tileFor = (key: AlertTypeKey): TypeTile | undefined => TYPE_TILES.find((t) => t.key === key);

/**
 * THE SAFETY TILE IS A LINK, NOT AN ANDON.
 *
 * There is no `safety.alert` model in Odoo, and the report WHS actually works
 * from lives outside it, so the tile hands straight over to that form in a new
 * tab: the operator keeps their place in the app and the report lands where
 * somebody reads it. The Safety andon type is archived, and its two months of
 * history — two andons — say why: a hazard is not a stopped line waiting for a
 * responder, it is a report.
 *
 * The link itself is `openSafetyIncidentReport` on the raise sheet, which the
 * + picker's Safety row already opens; the tile is a second door to the same
 * form, so one URL is repointed if the form ever moves.
 */
export const SAFETY_LINK_TILE = {
  label: 'tileSafetyT' as StringKey,
  routes: 'routeSafetyT' as StringKey,
  icon: 'hazard' as IconName,
  color: '#ff5a5f',
  tint: 'rgba(255,90,95,.13)',
} as const;

/** One cell of the raise grid: a tile that raises, or the Safety link that does not. */
export type GridItem = { kind: 'alert'; tile: TypeTile } | { kind: 'safety' };

/**
 * The grid, from the server's list of what this session may raise.
 *
 * Ordered by the server's list rather than by TYPE_TILES, so the grid's order
 * is one decision made in one place. A key the server sends and this build
 * has no tile for is dropped rather than drawn as a gap — the two lists are
 * deployed separately and a half-rendered tile on a raise grid is worse than
 * a missing one. A retired key is dropped too: the server never offers one,
 * and a stale list that did would be offering an archived type.
 *
 * Safety sits THIRD, after the two tiles that stop a line, where the grid has
 * always put it. It is not one of the server's tiles because it is not an
 * andon — see `SAFETY_LINK_TILE`.
 *
 * Test sits LAST whatever the server's order says. The server already lists
 * it last; this makes it a rule of the grid rather than a coincidence of the
 * list, because a grey tile whose promise is "nobody is coming" must never be
 * the first thing under a thumb.
 *
 * The fallback when home has not landed yet is every tile EXCEPT the gated
 * ones, not every tile: guessing generously here would flash the temporary
 * Test tile onto a shop-floor tablet for as long as the first request takes.
 */
export function raiseGrid(offered: readonly AlertTypeKey[] | undefined): GridItem[] {
  const tiles = offered
    ? offered.map((key) => tileFor(key)).filter((t): t is TypeTile => !!t && !t.retired)
    : TYPE_TILES.filter((t) => t.key !== 'test' && !t.retired);
  const test = tiles.filter((t) => t.key === 'test');
  const rest = tiles.filter((t) => t.key !== 'test');
  const items: GridItem[] = rest.map((tile) => ({ kind: 'alert' as const, tile }));
  items.splice(Math.min(2, items.length), 0, { kind: 'safety' });
  return [...items, ...test.map((tile) => ({ kind: 'alert' as const, tile }))];
}
